import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { NotificationApiService } from './notification-api.service';
import { NotificationStateService, FCMNotification } from './notification-state.service';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';

@Injectable({
  providedIn: 'root',
})
export class MessagingService {
  private messaging: any;
  private processedMessageIds = new Set<string>();
  private readonly FCM_TOKEN_REGISTERED_KEY = 'fcm_token_registered';

  constructor(
    private notificationApi: NotificationApiService,
    private notificationState: NotificationStateService
  ) {
    if (environment.firebase && environment.firebase.apiKey !== 'YOUR_API_KEY') {
      try {
        const app = initializeApp(environment.firebase);
        this.messaging = getMessaging(app);

        // Listen for foreground messages
        onMessage(this.messaging, (payload: any) => {
          // console.log('Message received. ', payload);

          // Process and emit the notification
          this.handleFCMMessage(payload);
        });
      } catch (error) {
        console.error('Firebase initialization error', error);
      }
    }
  }

  /**
   * Handle incoming FCM message
   */
  private handleFCMMessage(payload: any): void {
    try {
      const messageId = payload.messageId || this.generateMessageId();

      // Check if we've already processed this message
      if (this.processedMessageIds.has(messageId)) {
        console.log('Message already processed, skipping:', messageId);
        return;
      }

      // Mark message as processed
      this.processedMessageIds.add(messageId);

      // Clean up old message IDs (keep only last 100)
      if (this.processedMessageIds.size > 100) {
        const firstId = this.processedMessageIds.values().next().value;
        if (firstId) {
          this.processedMessageIds.delete(firstId);
        }
      }

      const fcmNotification: FCMNotification = {
        from: payload.from || '',
        collapseKey: payload.collapseKey,
        messageId: messageId,
        notification: {
          title: payload.notification?.title || 'New Notification',
          body: payload.notification?.body || '',
        },
        data: payload.data || {},
      };

      // Emit to notification state service
      this.notificationState.emitFCMNotification(fcmNotification);

      // Show browser notification if permission granted
      this.showBrowserNotification(fcmNotification);
    } catch (error) {
      console.error('Error handling FCM message:', error);
    }
  }

  /**
   * Show browser notification
   */
  private showBrowserNotification(notification: FCMNotification): void {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(notification.notification.title, {
        body: notification.notification.body,
        icon: '/favicon.ico',
        tag: notification.messageId,
      });
    }
  }

  /**
   * Generate a unique message ID
   */
  private generateMessageId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }

  /**
   * Request permission and retrieve the FCM token
   */
  async requestPermissionAndGetToken(patientId?: string, force = false) {
    if (!this.messaging) {
      console.warn(
        'Firebase Messaging not initialized. Please provide real credentials in environment.ts'
      );
      return null;
    }

    // Skip registration if already registered for the current session
    if (!patientId && !force) {
      const existingToken = localStorage.getItem(this.FCM_TOKEN_REGISTERED_KEY);
      if (existingToken) {
        return existingToken;
      }
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        const token = await getToken(this.messaging, {
          vapidKey: environment.firebase?.vapidKey,
        });

        if (token) {
          // If token matches already registered token for non-patient, skip calling API
          if (!patientId && !force && localStorage.getItem(this.FCM_TOKEN_REGISTERED_KEY) === token) {
            return token;
          }
          this.registerToken(token, patientId);
          return token;
        } else {
          console.warn('No registration token available. Request permission to generate one.');
          return null;
        }
      } else {
        console.warn('Notification permission denied');
        return null;
      }
    } catch (error) {
      console.error('Error getting FCM token:', error);
      return null;
    }
  }

  registerToken(token: string, patientId?: string) {
    const deviceType = this.getDeviceType();
    this.notificationApi
      .registerFCMToken({
        fcm_token: token,
        device_type: deviceType,
        patient_id: patientId,
      })
      .subscribe({
        next: (res) => {
          console.log('FCM Token registered successfully');
          if (!patientId) {
            localStorage.setItem(this.FCM_TOKEN_REGISTERED_KEY, token);
          }
        },
        error: (err) => console.error('Error registering FCM token', err),
      });
  }

  clearRegisteredToken(): void {
    localStorage.removeItem(this.FCM_TOKEN_REGISTERED_KEY);
  }

  private getDeviceType(): string {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return 'tablet';
    }
    if (
      /Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile|Kindle|NetFront|Silk-Accelerated|(hpw|web)OS|Fennec|Minimo|Opera M(obi|ini)|Blazer|Dolfin|Dolphin|Skyfire|Zune/i.test(
        ua
      )
    ) {
      return 'mobile';
    }
    return 'web';
  }
}
